import { useEffect, useMemo, useState } from 'react'
import { useRepos } from '../../state/repos'
import type { ListItem } from '../../db/repositories'
import type {
  BookContent,
  GarmentContent,
  GarmentSection,
} from '../../types/records'
import {
  computeWithInheritance,
  measurementsFromSize,
  type ComputeResult,
} from '../../geometry/computeGarment'
import { isDerivedCode } from '../../geometry/derived'
import { PiecePreview } from './PiecePreview'

const SECTION_LABELS: Record<GarmentSection, string> = {
  donna: 'Donna',
  uomo: 'Uomo',
  bambino: 'Bambino',
}

const CATEGORY_LABELS: Record<string, string> = {
  bodice: 'Corpetto',
  shirt: 'Camicia',
  blouse: 'Camicetta',
  dress: 'Abito',
  skirt: 'Gonna',
  trousers: 'Pantalone',
  jacket: 'Giacca',
  coat: 'Cappotto',
  vest: 'Gilet',
  sleeve: 'Manica',
  collar: 'Colletto',
  other: 'Altro',
}

type Mode = 'made-to-measure' | 'size-chart'

interface Props {
  preselectedGarmentId?: string
  onCreated: (projectId: string) => void
  onCancel: () => void
}

export function NewProject({ preselectedGarmentId, onCreated, onCancel }: Props) {
  const { garments, books, projects } = useRepos()
  const [garmentItems, setGarmentItems] = useState<ListItem<GarmentContent>[]>([])
  const [book, setBook] = useState<BookContent | null>(null)
  const [loaded, setLoaded] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  const [garmentId, setGarmentId] = useState<string>(preselectedGarmentId ?? '')
  const [garmentSearch, setGarmentSearch] = useState('')
  const [projectName, setProjectName] = useState('')
  const [mode, setMode] = useState<Mode>('made-to-measure')
  const [size, setSize] = useState<string>('')
  const [measurements, setMeasurements] = useState<Record<string, string>>({})
  const [ease, setEase] = useState<string>('4')
  const [notes, setNotes] = useState('')
  const [preview, setPreview] = useState<ComputeResult | null>(null)

  useEffect(() => {
    void (async () => {
      const [b, g] = await Promise.all([books.list(), garments.list()])
      setBook(b[0]?.content ?? null)
      setGarmentItems(g)
      setLoaded(true)
    })()
  }, [books, garments])

  const garment = useMemo(
    () => garmentItems.find(g => g.id === garmentId)?.content ?? null,
    [garmentId, garmentItems],
  )

  const searchResults = useMemo(() => {
    if (garmentId) return [] as typeof garmentItems
    const q = garmentSearch.trim().toLowerCase()
    const sorted = [...garmentItems].sort((a, b) =>
      a.content.name.localeCompare(b.content.name, 'it'),
    )
    if (!q) return sorted.slice(0, 50)
    return sorted
      .filter(g =>
        g.content.name.toLowerCase().includes(q) ||
        SECTION_LABELS[g.content.section].toLowerCase().includes(q) ||
        (CATEGORY_LABELS[g.content.category] ?? '').toLowerCase().includes(q),
      )
      .slice(0, 50)
  }, [garmentItems, garmentSearch, garmentId])

  const codeMap = useMemo(() => {
    const m = new Map<string, string>()
    for (const x of book?.measurementsBase ?? []) m.set(x.code, x.italianName)
    return m
  }, [book])

  const availableSizes = useMemo(() => {
    if (!book || !garment) return [] as string[]
    if (garment.section === 'donna') return book.sizesWomen.sizes
    if (garment.section === 'uomo') return book.sizesMen.sizes
    return []
  }, [book, garment])

  // Quando cambia capo o modalità, reset misure
  useEffect(() => {
    setMeasurements({})
    setSize('')
    setPreview(null)
    if (garment && !projectName) {
      setProjectName(garment.name)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [garmentId])

  // Auto-fill da piano taglie
  useEffect(() => {
    if (mode !== 'size-chart' || !size || !book || !garment) return
    const fromSize = measurementsFromSize(size, garment.section, book)
    const next: Record<string, string> = {}
    for (const code of garment.measurementsRequired) {
      const v = fromSize[code]
      if (typeof v === 'number') next[code] = String(v)
    }
    setMeasurements(next)
  }, [mode, size, book, garment])

  function setMeasure(code: string, value: string) {
    setMeasurements(prev => ({ ...prev, [code]: value }))
  }

  function buildNumericMeasurements(): { values: Record<string, number>; missing: string[] } {
    const values: Record<string, number> = {}
    const missing: string[] = []
    for (const code of garment?.measurementsRequired ?? []) {
      if (isDerivedCode(code)) continue // Tg./vest: derivati, non chiesti
      const raw = (measurements[code] ?? '').trim().replace(',', '.')
      const num = parseFloat(raw)
      if (Number.isFinite(num)) {
        values[code] = num
      } else {
        missing.push(code)
      }
    }
    return { values, missing }
  }

  function runPreview() {
    setError(null)
    if (!garment) return
    const { values, missing } = buildNumericMeasurements()
    if (missing.length > 0) {
      setError(`Mancano valori per: ${missing.join(', ')}`)
      return
    }
    const easeNum = parseFloat((ease || '0').replace(',', '.'))
    const result = computeWithInheritance(
      garment,
      garmentItems.map(g => g.content),
      values,
      Number.isFinite(easeNum) ? easeNum : 0,
    )
    setPreview(result)
  }

  async function save() {
    setError(null)
    if (!garment) {
      setError('Seleziona un capo')
      return
    }
    if (!projectName.trim()) {
      setError('Inserisci un nome per il progetto')
      return
    }
    const { values, missing } = buildNumericMeasurements()
    if (missing.length > 0) {
      setError(`Mancano valori per: ${missing.join(', ')}`)
      return
    }

    setSaving(true)
    try {
      const id = await projects.create(garmentId, mode, {
        name: projectName.trim(),
        garmentName: garment.name,
        developmentType: mode,
        sourceSizeLabel: mode === 'size-chart' ? size : undefined,
        measurements: values,
        additionalNotes: notes,
      })
      onCreated(id)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore salvataggio')
    } finally {
      setSaving(false)
    }
  }

  if (!loaded) return <p className="muted">Caricamento…</p>

  if (!book || garmentItems.length === 0) {
    return (
      <section className="tab-section">
        <button className="back-button" onClick={onCancel}>← Indietro</button>
        <div className="empty-state">
          <p>Importa prima il libro e i capi.</p>
        </div>
      </section>
    )
  }

  return (
    <section className="tab-section detail-section">
      <button className="back-button" onClick={onCancel}>← Annulla</button>

      <header className="detail-header card">
        <h3>Nuovo progetto</h3>
        <p className="header-meta">
          Scegli un capo, le misure e crea un progetto. I valori delle formule
          vengono calcolati automaticamente.
        </p>
      </header>

      <div className="detail-block">
        <h4>① Capo</h4>
        {garment ? (
          <div className={`selected-garment card section-${garment.section}`}>
            <div className="selected-garment-info">
              <div className="garment-card-title">{garment.name}</div>
              <div className="garment-card-badges">
                <span className={`badge section-${garment.section}`}>
                  {SECTION_LABELS[garment.section]}
                </span>
                <span className="badge category">
                  {CATEGORY_LABELS[garment.category] ?? garment.category}
                </span>
                <span className="badge scale">
                  pp. {garment.startPage}
                  {garment.startPage !== garment.endPage ? `-${garment.endPage}` : ''}
                </span>
                <span className="badge muted">
                  {garment.measurementsRequired.length} misure
                </span>
              </div>
            </div>
            <button
              className="secondary"
              onClick={() => {
                setGarmentId('')
                setGarmentSearch('')
              }}
            >
              Cambia
            </button>
          </div>
        ) : (
          <>
            <input
              type="search"
              placeholder="Cerca capo per nome, sezione o categoria…"
              value={garmentSearch}
              onChange={e => setGarmentSearch(e.target.value)}
              autoFocus
              style={{ marginBottom: '0.6rem' }}
            />
            <p className="muted" style={{ margin: '0 0 0.5rem' }}>
              {garmentSearch.trim()
                ? `${searchResults.length} ${searchResults.length === 1 ? 'risultato' : 'risultati'}`
                : `${garmentItems.length} capi disponibili — digita per filtrare`}
            </p>
            <ul className="garment-picker-list">
              {searchResults.map(g => (
                <li
                  key={g.id}
                  className={`garment-picker-item section-${g.content.section}`}
                  onClick={() => {
                    setGarmentId(g.id)
                    if (!projectName) setProjectName(g.content.name)
                  }}
                  role="button"
                  tabIndex={0}
                  onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      setGarmentId(g.id)
                      if (!projectName) setProjectName(g.content.name)
                    }
                  }}
                >
                  <div className="picker-name">{g.content.name}</div>
                  <div className="picker-meta">
                    <span className={`badge section-${g.content.section}`}>
                      {SECTION_LABELS[g.content.section]}
                    </span>
                    <span className="muted">
                      {CATEGORY_LABELS[g.content.category] ?? g.content.category}
                      {' · '}pp. {g.content.startPage}
                      {g.content.startPage !== g.content.endPage ? `-${g.content.endPage}` : ''}
                    </span>
                  </div>
                </li>
              ))}
              {searchResults.length === 0 && (
                <li className="picker-empty muted">
                  Nessun capo trovato per "{garmentSearch}"
                </li>
              )}
            </ul>
          </>
        )}
      </div>

      {garment && (
        <>
          <div className="detail-block">
            <h4>② Nome del progetto</h4>
            <label>
              Nome
              <input
                type="text"
                value={projectName}
                onChange={e => setProjectName(e.target.value)}
                placeholder={garment.name}
              />
            </label>
          </div>

          <div className="detail-block">
            <h4>③ Modalità sviluppo</h4>
            <div className="row">
              <button
                className={mode === 'made-to-measure' ? '' : 'secondary'}
                onClick={() => setMode('made-to-measure')}
              >
                Su misura
              </button>
              <button
                className={mode === 'size-chart' ? '' : 'secondary'}
                onClick={() => setMode('size-chart')}
              >
                Piano taglie
              </button>
            </div>

            {mode === 'size-chart' && (
              <label style={{ marginTop: '1rem' }}>
                Taglia di riferimento
                <select value={size} onChange={e => setSize(e.target.value)}>
                  <option value="">— scegli taglia —</option>
                  {availableSizes.map(s => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </label>
            )}
          </div>

          <div className="detail-block">
            <h4>④ Misure</h4>
            <p className="muted">
              {mode === 'size-chart' && size
                ? 'Auto-compilate dalla taglia. Modifica se necessario.'
                : 'Inserisci le misure in centimetri (anche con virgola).'}
            </p>
            <div className="measurements-grid">
              {garment.measurementsRequired.filter(c => !isDerivedCode(c)).map(code => (
                <label key={code} className="measurement-input">
                  <span className="m-code">{code}</span>
                  <span className="m-name">{codeMap.get(code) ?? '—'}</span>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={measurements[code] ?? ''}
                    onChange={e => setMeasure(code, e.target.value)}
                    placeholder="cm"
                  />
                </label>
              ))}
            </div>
            <label style={{ marginTop: '1rem', maxWidth: 200 }}>
              Vestibilità (cm)
              <input
                type="text"
                inputMode="decimal"
                value={ease}
                onChange={e => setEase(e.target.value)}
              />
            </label>
          </div>

          <div className="detail-block">
            <h4>⑤ Note aggiuntive</h4>
            <p className="muted">
              Eventuali precisazioni, varianti, o passaggi non chiari nel libro.
            </p>
            <textarea
              rows={4}
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="Note opzionali..."
              style={{ resize: 'vertical' }}
            />
          </div>

          <div className="detail-block">
            <h4>⑥ Anteprima calcolo</h4>
            <div className="row">
              <button onClick={runPreview} className="secondary">
                Calcola anteprima
              </button>
            </div>
            {preview && (
              <PreviewSummary preview={preview} />
            )}
          </div>

          {error && <div className="status status-err">{error}</div>}

          <div className="row detail-actions">
            <button onClick={save} disabled={saving}>
              {saving ? 'Salvataggio…' : 'Crea progetto'}
            </button>
            <button className="secondary" onClick={onCancel} disabled={saving}>
              Annulla
            </button>
          </div>
        </>
      )}
    </section>
  )
}

function PreviewSummary({ preview }: { preview: ComputeResult }) {
  const totalPoints = preview.pieces.reduce((s, p) => s + p.points.length, 0)
  const errCount = preview.errors.length

  return (
    <div className="preview-summary" style={{ marginTop: '1rem' }}>
      <div className={errCount > 0 ? 'status status-err' : 'status status-ok'}>
        {errCount === 0
          ? `Calcolo completato: ${totalPoints} punti calcolati su ${preview.pieces.length} pezzi.`
          : `Calcolo parziale: ${totalPoints} punti calcolati, ${errCount} errori (sotto).`}
      </div>
      {preview.pieces.map(p => (
        <div key={p.id} className="piece-block card" style={{ marginTop: '0.8rem' }}>
          <div className="piece-block-header">
            <strong>{p.italianLabel}</strong>
            <span className="muted">
              {p.points.length} punti · {p.segments.length} segmenti
            </span>
          </div>
          {p.points.length > 0 && <PiecePreview piece={p} />}
          <details className="piece-points-details">
            <summary className="muted">Vedi tabella punti</summary>
            <table className="points-table">
              <thead>
                <tr>
                  <th>Punto</th>
                  <th>X (cm)</th>
                  <th>Y (cm)</th>
                </tr>
              </thead>
              <tbody>
                {p.points.map(pt => (
                  <tr key={pt.label}>
                    <td><code>{pt.label}</code></td>
                    <td>{pt.x.toFixed(2)}</td>
                    <td>{pt.y.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </details>
        </div>
      ))}
      {errCount > 0 && (
        <details open style={{ marginTop: '0.6rem' }}>
          <summary><strong>Errori ({errCount})</strong></summary>
          <ul className="muted" style={{ marginTop: '0.4rem' }}>
            {preview.errors.map((e, i) => (
              <li key={i}>
                {e.pieceLabel} · punto {e.pointLabel} · {e.axis}: <code>{e.formula}</code> — {e.message}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  )
}
